import { TableProps } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';

import TableWrapper from '@/components/atoms/TableWrapper';
import CustomTable from '@/components/atoms/CustomTable';
import { Filters } from './Filters';
import {
  setSubscriptionFilters,
  useSubscriptionFilters,
} from '@/store/slices/reportFiltersSlice';

import { RequestDetailsDrawer } from './RequestDetailsDrawer';
import { useGetSubscriptionReportQuery } from '@/store/services/reports';
import {
  setModalId,
  setPage,
  usePublicState,
} from '@/store/slices/publicSlice';

export interface SubscriptionTableRow {
  id: string | number;
  rowId?: string;
  userId: string;
  username: string;
  name: string;
  planName: string;
  period: string;
  joinDate: string;
  method: string;
  amount: string | number;
}

const RequestsTable = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { search, page } = usePublicState();
  const filters = useSubscriptionFilters();
  const dispatch = useDispatch();

  const searchValue = search?.subscriptionReportSearch || '';

  const {
    data: reportsList,
    isLoading,
    isFetching,
  } = useGetSubscriptionReportQuery(
    {
      page: page,
      search: searchValue,
      dateFrom: filters?.fromDate,
      dateTo: filters?.toDate,
      periodType: filters?.periodType,
      planType: filters?.planType,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const columns: TableProps<SubscriptionTableRow>['columns'] = [
    {
      title: t('reports.userId', 'User ID'),
      dataIndex: 'userId',
      key: 'userId',
      render: (val) => val || '-',
    },
    {
      title: t('reports.username', 'Username'),
      dataIndex: 'username',
      key: 'username',
      render: (val) => val || '-',
    },
    {
      title: t('reports.name', 'Name'),
      dataIndex: 'name',
      key: 'name',
      render: (val) => val || '-',
    },
    {
      title: t('reports.planName', 'Plan name'),
      dataIndex: 'planName',
      key: 'planName',
      render: (val) => val || '-',
    },
    {
      title: t('reports.period', 'Period'),
      dataIndex: 'period',
      key: 'period',
      render: (val) => val || '-',
    },
    {
      title: t('reports.joinDate', 'Join date'),
      key: 'joinDate',
      dataIndex: 'joinDate',
      render: (value) => {
        return (
          <p>
            {value && dayjs(value).isValid()
              ? dayjs(value).format('DD MMM YYYY')
              : value || '-'}
          </p>
        );
      },
    },
    {
      title: t('reports.method', 'Method'),
      dataIndex: 'method',
      key: 'method',
      render: (val) => val || '-',
    },
    {
      title: t('reports.amount', 'Amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (val) => val || '-',
    },
  ];

  dayjs.locale(language);

  return (
    <>
      <TableWrapper
        filters={<Filters />}
        title={t('reports.requests', 'Requests')}
        subTitle={t(
          'reports.subscription.subTitle',
          'A detailed list of all user subscriptions'
        )}
      >
        <CustomTable<SubscriptionTableRow>
          rowKey="rowId"
          loading={isLoading || isFetching}
          currentPage={page || 0}
          pageSize={filters?.limit || 10}
          totalElements={reportsList?.payload?.totalElements || 0}
          onPageChange={(pageNumber, pageSize) => {
            dispatch(setPage(pageNumber - 1));
            dispatch(
              setSubscriptionFilters({
                limit: pageSize,
              })
            );
          }}
          onRowClick={(_, record) => {
            const id = record.id;
            dispatch(setModalId({ id }));
          }}
          columns={columns}
          dataSource={
            reportsList?.payload?.content?.map((item: any, idx: number) => ({
              id: item?.profileId,
              rowId: `${item?.profileId}-${item?.requestDate}-${idx}`,
              userId: item?.userSerialId,
              username: item?.userName,
              name: item?.name,
              planName: item?.planName,
              period: item?.period,
              joinDate: item?.requestDate,
              method: item?.paymentMethod,
              amount: `${item?.amount?.price} ${item?.amount?.currency}`,
            })) || []
          }
        />
      </TableWrapper>

      <RequestDetailsDrawer
        listOfIds={
          (reportsList?.payload?.content?.map((el: any) =>
            String(el?.profileId)
          ) || []) as string[]
        }
      />
    </>
  );
};

export default RequestsTable;
